import { PhotoHeroAction } from 'features/fim/photos/PhotoHeroAction'
import { PhotoRemoveButton } from 'features/fim/photos/PhotoRemoveButton'
import { SubjectPhotoCard } from 'features/fim/photos/SubjectPhotoCard'
import { SubjectPhotoUploader } from 'features/fim/photos/SubjectPhotoUploader'
import type { SubjectPhoto } from 'features/fim/photos/types'
import { useQueuedPhotoIds } from 'features/fim/photos/usePhotoUploadQueue'
import {
  type PendingPhotoAction,
  useSubjectPhotoActions,
} from 'features/fim/photos/useSubjectPhotoActions'
import { useSubjectPhotos } from 'features/fim/photos/useSubjectPhotos'
import type { FimSubjectRecord } from 'features/fim/types'
import { type FC, type ReactNode, useCallback, useMemo } from 'react'

const ListedPhotoCard: FC<{
  photo: SubjectPhoto
  heroAction: ReactNode
  pending: PendingPhotoAction | null
  onRemove: (photoId: string) => void
}> = ({ photo, heroAction, pending, onRemove }) => {
  const photoId = photo.entry.id
  const remove = useCallback(() => onRemove(photoId), [onRemove, photoId])

  return (
    <SubjectPhotoCard
      photo={photo}
      action={
        <>
          {heroAction}
          <PhotoRemoveButton
            removing={
              pending?.photoId === photoId && pending.action === 'remove'
            }
            disabled={pending !== null}
            onRemove={remove}
          />
        </>
      }
    />
  )
}

export const PhotoModeSubjectPhotos: FC<{
  siteId: string
  subject: FimSubjectRecord
}> = ({ siteId, subject }) => {
  const { photos, refresh } = useSubjectPhotos(siteId, subject)
  const entries = useMemo(() => photos.map((photo) => photo.entry), [photos])
  const { heroId, pending, makeHero, removePhoto } = useSubjectPhotoActions(
    siteId,
    subject,
    entries,
    refresh
  )
  const entryIds = useMemo(
    () => new Set(entries.map((entry) => entry.id)),
    [entries]
  )
  const queuedIds = useQueuedPhotoIds(siteId, subject.subjectUid)

  const listed = useMemo(
    () =>
      photos
        .filter((photo) => !queuedIds.has(photo.entry.id))
        .sort(
          (a, b) =>
            Number(b.entry.id === heroId) - Number(a.entry.id === heroId)
        ),
    [heroId, photos, queuedIds]
  )

  const renderHeroAction = useCallback(
    (photoId: string) =>
      entryIds.has(photoId) && (
        <PhotoHeroAction
          photoId={photoId}
          isHero={photoId === heroId}
          saving={
            pending?.photoId === photoId && pending.action === 'make_hero'
          }
          disabled={pending !== null}
          onMakeHero={makeHero}
        />
      ),
    [entryIds, heroId, makeHero, pending]
  )

  return (
    <>
      {listed.map((photo) => (
        <ListedPhotoCard
          key={photo.entry.id}
          photo={photo}
          heroAction={renderHeroAction(photo.entry.id)}
          pending={pending}
          onRemove={removePhoto}
        />
      ))}
      <SubjectPhotoUploader
        siteId={siteId}
        subject={subject}
        source='subject_panel'
        onAttached={refresh}
        retainAttached
        renderAttachedAction={renderHeroAction}
      />
    </>
  )
}
